import {
  isDefinedArray,
  isDefinedString,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import queryString from "query-string";
import { isNumber, memoize, orderBy } from "lodash";
import { observable } from "mobx";

import getUserStore from "..";
import { apiCallChangeUserData } from "../../../api/user-api/api-call-change-user-data";
import { apiCallGetUserEditProfile } from "../../../api/user-api/api-call-get-user-edit-profile";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import { StringOrNumber, TKeyEntries } from "../../../Types";
import {
  EDUCATION_ENTRIES,
  WORK_ENTRIES
} from "../../../constants/picker-entries-constants";

class UserEditProfileStore {
  userToEditCurrentGender = observable.box<string>("");

  setUserToEditCurrentGender = (gender: string) => {
    this.userToEditCurrentGender.set(gender);
  };

  userEditProfileDataArchive = observable.box<
    {
      key: string;
      value: StringOrNumber;
      description: string;
      dateChange: string;
      changedBy: string;
    }[]
  >([]);
  userEditProfileData = observable.box<
    {
      key: string;
      value: StringOrNumber;
      numberOfChange: number;
    }[]
  >([]);
  userStatus = observable.box<string>("");
  userUUID = observable.box<string>("");

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const searchID = queryString.parse(search).id;
      if (isDefinedString(searchID) && searchID !== "0") {
        const searchIDNumber = parseInt(searchID, 10);
        getUserStore().setUserIDInput(searchIDNumber);
        await getUserEditProfileStore().getUserEditProfile();
      }
    }
  };

  getUserEditProfileFromDatabase = async () => {
    const userId = getUserStore().userIdInput.get();
    if (!isNumber(userId) || userId === -1) {
      getCorePopUpStore().showAlertMessage("WRONG_USER_ID");
      return;
    }
    getCoreUIStore().setIsLoading("USER_EDIT_PROFILE", true);
    const { data, code, status } = await apiCallGetUserEditProfile({
      userId
    });
    if (status === "ERROR") {
      this.userEditProfileDataArchive.set([]);
      this.userEditProfileData.set([]);
      getCoreUIStore().setIsLoading("USER_EDIT_PROFILE", false);
      return getCorePopUpStore().showAlertMessage(code);
    }
    const archivedData = reduce(
      () => data.archiveData,
      [
        {
          key: "",
          value: "",
          description: "",
          dateChange: "",
          changedBy: ""
        }
      ]
    );
    const userEditData = reduce(
      () => data.userEditData,
      [
        {
          key: "",
          value: "",
          numberOfChange: -1
        }
      ]
    );
    const userStatus = reduce(() => data.status, "");
    const gender = reduce(() => data.gender, "");
    const userUUID = reduce(() => data.userUUID, "");
    this.userEditProfileDataArchive.set(archivedData);
    this.userEditProfileData.set(userEditData);
    this.userStatus.set(userStatus);
    this.userUUID.set(userUUID);
    this.setUserToEditCurrentGender(gender);
    getCoreUIStore().setIsLoading("USER_EDIT_PROFILE", false);
  };

  getUserEditProfile = async () => {
    return await this.getUserEditProfileFromDatabase();
  };

  editUserDataOnDatabase = async (
    userUUID: string,
    key: string,
    value: number | string
  ) => {
    getCoreUIStore().setIsLoading("USER_EDIT_PROFILE", true);
    const { status, code, data } = await apiCallChangeUserData({
      userUUID,
      key,
      value
    });
    getCoreUIStore().setIsLoading("USER_EDIT_PROFILE", false);
    if (status === "OK") {
      const newStatus = data;
      getCorePopUpStore().showSuccessMessage("USER_DATA_CHANGED");

      return newStatus;
    } else {
      this.userEditProfileDataArchive.set([]);
      this.userEditProfileData.set([]);
      getCorePopUpStore().showAlertMessage(code);
      return "ERROR";
    }
  };

  getEntryValue = (entries: TKeyEntries[], editValue: StringOrNumber) => {
    const parsedEditValue = parseInt(`${editValue}`, 10);
    if (isPositiveNumber(parsedEditValue)) {
      return entries.find(
        (entry) => parseInt(entry.value, 10) === parsedEditValue
      ).value;
    } else {
      return reduce(
        () => entries.find((entry) => entry.label === editValue).value,
        "-1"
      );
    }
  };

  ifKeyIsWorkOrEducation = (key: string, editValue: StringOrNumber) => {
    switch (key) {
      case "EDUCATION":
        return this.getEntryValue(EDUCATION_ENTRIES, editValue);
      case "WORK":
        return this.getEntryValue(WORK_ENTRIES, editValue);
      default:
        return editValue;
    }
  };

  editUserData = async ({
    key,
    value
  }: {
    key: string;
    value: StringOrNumber;
  }) => {
    const userUUID = this.userUUID.get();
    const editValue = this.ifKeyIsWorkOrEducation(key, value);
    const editedFilteredArray = this.userEditProfileData
      .get()
      .filter((editedValues) => {
        return (
          `${editedValues.key}` === `${key}` &&
          `${editedValues.value}` === `${editValue}`
        );
      });
    if (isDefinedArray(editedFilteredArray)) {
      getCorePopUpStore().showAlertMessage("ALREADY_EXISTING_VALUE");
      return;
    }
    await this.editUserDataOnDatabase(userUUID, key, editValue);
    await this.getUserEditProfile();
    return;
  };

  getUserEditDataArrays = () => {
    const editProfileArray = reduce(
      () => getUserEditProfileStore().userEditProfileData.get(),
      []
    );
    const profileHistoryArray = reduce(
      () => getUserEditProfileStore().userEditProfileDataArchive.get(),
      []
    );
    const orderedprofileHistoryArray = orderBy(
      profileHistoryArray,
      ["dateChange"],
      ["desc"]
    );
    return {
      editProfileDataArray: editProfileArray,
      profileHistoryDataArray: orderedprofileHistoryArray
    };
  };
}

const getUserEditProfileStore = memoize(
  () => new UserEditProfileStore(),
  () => 1
);
export default getUserEditProfileStore;
